import React, { useState, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import { useNavigateToPost } from '../../hooks/useNavigateToPost';
import { useConversation } from '../../context/ConversationProvider';
import { useMessages } from '../../context/MessageProvider';
import { usePresence } from '../../context/UserPresenceProvider';
import { useUIState } from '../../context/UIStateProvider';
import { openShareModal } from '../../../../features/share';
import SuspiciousLinkWarning from '../shared/SuspiciousLinkWarning';
import MessageContextMenu from '../shared/MessageContextMenu';
import { formatChatMessageTime } from '../../../../utils/dateFormat';
import MediaViewer from '../../../../components/common/MediaViewer';
import { IconArrowForwardUp } from '@tabler/icons-react';
import './ChatWindow.css';


// URL regex pattern for matching http, https, and www URLs
const URL_PATTERN = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;

// FIX H-3: Block dangerous URL protocols explicitly
// eslint-disable-next-line no-script-url
const DANGEROUS_PROTOCOLS = ['javascript:', 'data:', 'vbscript:', 'blob:'];

// FIX 7.1: Validate URLs with URL constructor; block non-HTTPS and dangerous protocols
const isUrl = (str) => {
  if (!/^(https?:\/\/|www\.)/i.test(str)) return false;
  try {
    const url = new URL(str.startsWith('http') ? str : `https://${str}`);
    // Block dangerous protocols
    if (DANGEROUS_PROTOCOLS.some(p => url.protocol === p)) return false;
    return url.protocol === 'https:' || url.protocol === 'http:';
  } catch {
    return false;
  }
};

// FIX D1: Memoize MessageBubble to prevent unnecessary re-renders for every message
// on any context change. Custom comparison checks message reference, isGroup, and highlightedMessageId.
const arePropsEqual = (prevProps, nextProps) => {
  return (
    prevProps.message === nextProps.message &&
    prevProps.isGroup === nextProps.isGroup
  );
};

const MessageBubble = React.memo(({ message, isGroup = false }) => {
  const [contextMenuVisible, setContextMenuVisible] = useState(false);
  const [contextMenuPosition, setContextMenuPosition] = useState({ x: 0, y: 0 });
  const [sharedPostMenuVisible, setSharedPostMenuVisible] = useState(false);
  const [sharedPostMenuPosition, setSharedPostMenuPosition] = useState({ x: 0, y: 0 });
  const longPressTimerRef = useRef(null);

  const handleSharedPostLongPress = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const touch = e.touches ? e.touches[0] : e;
    setSharedPostMenuPosition({ x: touch.clientX, y: touch.clientY });
    setSharedPostMenuVisible(true);
  };

  const handleSharedPostTouchStart = (e) => {
    longPressTimerRef.current = setTimeout(() => {
      handleSharedPostLongPress(e);
    }, 500);
  };

  const handleSharedPostTouchEnd = () => {
    if (longPressTimerRef.current) {
      clearTimeout(longPressTimerRef.current);
      longPressTimerRef.current = null;
    }
  };

  const handleSharedPostForward = () => {
    setForwardingMessage({
      messageId: message.id,
      content: message.content || '',
      sharedPost: message.sharedPost || null,
      senderName,
    });
    setSharedPostMenuVisible(false);
  };

  const handleSharedPostShare = () => {
    if (message.sharedPost) {
      dispatch(openShareModal({
        item: {
          id: message.sharedPost.id,
          title: message.sharedPost.title || '',
          content: message.sharedPost.content || '',
          imageUrl: message.sharedPost.imageUrl || '',
          username: message.sharedPost.authorName || '',
          userPhotoURL: message.sharedPost.authorPhotoURL || '',
          userProfession: message.sharedPost.authorProfession || '',
          timestamp: message.sharedPost.timestamp || '',
          type: 'post',
          sharedPostId: message.sharedPost.sharedPostId || null,
          sharedPostData: message.sharedPost.sharedPostData || null,
        },
        type: 'post',
      }));
    }
    setSharedPostMenuVisible(false);
  };
  const [showDetails, setShowDetails] = useState(false);
  const [isPreviewOpen, setIsPreviewOpen] = useState(false);

  const detailedTime = formatChatMessageTime(message.createdAt);

  const { navigateToPost } = useNavigateToPost();

  // Use modular contexts instead of legacy useChat
  const { currentUser, conversations, activeConversationId } = useConversation();
  const { deleteMessage, scrollToMessage } = useMessages();
  const { presenceByUserId } = usePresence();
  const { setReplyingTo, setEditingMessage, setForwardingMessage, highlightedMessageId } = useUIState();
  const dispatch = useDispatch();
  const isMine = currentUser && message.senderId === currentUser.uid;
  const isHighlighted = highlightedMessageId === message.id;


  // Handle deleted messages early
  if (message.deleted) {
    return (
      <div className="chat-bubble chat-bubble--deleted">
        <span className="chat-bubble__deleted-icon">🗑️</span>
        <span className="chat-bubble__deleted-text">This message was deleted</span>
      </div>
    );
  }

  // Get sender info for group chats
  const senderName = presenceByUserId[message.senderId]?.displayName ||
    presenceByUserId[message.senderId]?.username ||
    'Someone';

  const linkInfo = message.linkInfo;
  const risks = Array.isArray(linkInfo?.risks) ? linkInfo.risks : [];
  const hasHigh = risks.some(r => r && r.riskLevel === 'high');
  const hasMedium = risks.some(r => r && r.riskLevel === 'medium');
  const showWarning = !!linkInfo?.hasLinks && (hasHigh || hasMedium);
  const warningMessage = hasHigh
    ? 'This link was flagged as dangerous and may be blocked.'
    : 'This link looks potentially unsafe. Please be careful before opening it.';

  // Compute 3-state tick status: 'sent' → 'delivered' → 'seen'
  // ✓ = sent (message exists in Firestore)
  // ✓✓ gray = delivered (recipient's app loaded the message)
  // ✓✓ blue = seen (recipient opened the chat after this message)
  let tickStatus = 'sent'; // default: single tick
  try {
    const conv = (conversations || []).find(c => c.id === activeConversationId) || null;
    if (isMine && conv && message?.createdAt) {
      const memberIds = Array.isArray(conv.memberIds) ? conv.memberIds : [];
      const otherId = currentUser?.uid ? memberIds.find(id => id !== currentUser.uid) : null;

      // Check if delivered (other user's app loaded the message)
      const deliveredTo = message.deliveredTo || {};
      const isDelivered = otherId && deliveredTo[otherId];

      // Check if seen (other user's lastSeenAt >= message.createdAt)
      const lastSeenRaw = otherId && conv.lastSeenAt ? conv.lastSeenAt[otherId] : null;
      const lastSeen = lastSeenRaw && lastSeenRaw.toDate ? lastSeenRaw.toDate() : lastSeenRaw || null;
      const isSeen = lastSeen && message.createdAt && message.createdAt <= lastSeen;

      if (isSeen) {
        tickStatus = 'seen'; // ✓✓ blue
      } else if (isDelivered) {
        tickStatus = 'delivered'; // ✓✓ gray
      } else {
        tickStatus = 'sent'; // ✓
      }
    }
  } catch (_) {
    // no-op
  }



  const renderContent = (text) => {
    if (!text) return null;

    // Split text by URLs while preserving the URLs in the result
    const parts = String(text).split(URL_PATTERN);

    return parts.map((part, idx) => {
      if (!part) return null; // Skip empty parts from split

      // Check if this part is a URL
      if (isUrl(part)) {
        // Ensure URL has protocol for href
        const href = part.startsWith('http') ? part : `https://${part}`;
        return (
          <a
            key={idx}
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            className="chat-bubble__link"
            onClick={(e) => e.stopPropagation()}
          >
            {part}
          </a>
        );
      }
      // Check if this part is a mention
      if (part.startsWith('@') && part.length > 1 && !/\s/.test(part)) {
        return (
          <span
            key={idx}
            className="chat-bubble__mention"
            style={{ color: '#2563eb', fontWeight: 500 }}
          >
            {part}
          </span>
        );
      }
      // Regular text
      return <span key={idx}>{part}</span>;
    });
  };

  const handleContextMenu = (e) => {
    e.preventDefault();
    setContextMenuPosition({ x: e.clientX, y: e.clientY });
    setContextMenuVisible(true);
  };

  const handleReply = () => {
    setReplyingTo({
      messageId: message.id,
      senderId: message.senderId,
      senderName,
      content: message.content?.substring(0, 100) || '[Media]',
      timestamp: message.createdAt
    });
    setContextMenuVisible(false);
  };

  const handleEdit = () => {
    setEditingMessage(message);
    setContextMenuVisible(false);
  };

  const handleDelete = () => {
    if (window.confirm('Delete this message?')) {
      deleteMessage(message.id);
    }
    setContextMenuVisible(false);
  };

  const handleForward = () => {
    setForwardingMessage({
      messageId: message.id,
      content: message.content || '',
      imageUrl: message.imageUrl || null,
      fileUrl: message.fileUrl || null,
      fileName: message.fileName || null,
      senderName,
    });
    setContextMenuVisible(false);
  };

  return (
    <div className={`chat-message-container`}>
      {/* FIX H-5: Session header removed here — rendered by ChatWindow renderRow to avoid duplication */}

      <div
        className={`chat-message-row ${isMine ? 'chat-message-row--mine' : 'chat-message-row--theirs'}`}
        onClick={() => setShowDetails(!showDetails)}
      >
        <div
          className={`chat-bubble ${isMine ? 'chat-bubble--mine' : 'chat-bubble--theirs'} ${message.status ? `chat-bubble--${message.status}` : ''} ${isHighlighted ? 'chat-bubble--highlighted' : ''}`}
          aria-label={isMine ? 'Your message' : 'Received message'}
          onContextMenu={handleContextMenu}
          data-message-id={message.id}
        >
          {isGroup && !isMine && (
            <div className="chat-bubble__sender-name">{senderName}</div>
          )}

          {/* Reply-to preview */}
          {message.replyTo && (
            <div
              className="chat-bubble__reply-preview"
              onClick={(e) => {
                e.stopPropagation();
                scrollToMessage(message.replyTo.messageId);
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  scrollToMessage(message.replyTo.messageId);
                }
              }}
              role="button"
              tabIndex={0}
            >
              <div className="chat-bubble__reply-bar" />
              <div className="chat-bubble__reply-content">
                <div className="chat-bubble__reply-sender">
                  {message.replyTo.senderName || 'Someone'}
                </div>
                <div className="chat-bubble__reply-text">
                  {message.replyTo.content?.substring(0, 60) || '[Media]'}
                  {message.replyTo.content?.length > 60 ? '...' : ''}
                </div>
              </div>
            </div>
          )}

          <SuspiciousLinkWarning visible={showWarning} message={warningMessage} />
          {message.imageUrl && (
            <div className="chat-bubble__image-wrapper" onClick={(e) => {
              e.stopPropagation();
              setIsPreviewOpen(true);
            }} style={{ cursor: 'pointer' }}>
              <img
                src={message.imageUrl}
                alt="Sent attachment"
                className="chat-bubble__image"
                loading="lazy"
              />
            </div>
          )}
          {isPreviewOpen && (
            <MediaViewer 
              mediaUrls={[message.imageUrl]} 
              mediaType="image" 
              onClose={() => setIsPreviewOpen(false)} 
            />
          )}
          {message.fileUrl && (
            <div className="chat-bubble__file">
              <a
                href={message.fileUrl}
                target="_blank"
                rel="noopener noreferrer"
                download
                className="chat-bubble__file-link"
                onClick={(e) => e.stopPropagation()}
              >
                {message.fileName || 'Attachment'}
              </a>
            </div>
          )}
          {message.sharedPost && (
            <a
              href={message.sharedPost.url || '#'}
              className="chat-bubble__shared-post"
              onClick={(e) => {
                e.preventDefault();
                e.stopPropagation();
                navigateToPost(message.sharedPost.url);
              }}
              onContextMenu={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setSharedPostMenuPosition({ x: e.clientX, y: e.clientY });
                setSharedPostMenuVisible(true);
              }}
              onTouchStart={handleSharedPostTouchStart}
              onTouchEnd={handleSharedPostTouchEnd}
              onTouchMove={handleSharedPostTouchEnd}
            >
              {message.sharedPost.imageUrl && (
                <div className="chat-bubble__shared-post-image">
                  <img src={message.sharedPost.imageUrl} alt={message.sharedPost.title || 'Shared post image'} loading="lazy" />
                </div>
              )}
              <div className="chat-bubble__shared-post-content">
                {message.sharedPost.authorPhotoURL && (
                  <div className="chat-bubble__shared-post-author-avatar">
                    <img src={message.sharedPost.authorPhotoURL} alt={message.sharedPost.authorName || 'Author'} />
                  </div>
                )}
                <div className="chat-bubble__shared-post-title">
                  {message.sharedPost.title || 'View Post'}
                </div>
                {message.sharedPost.content && (
                  <div className="chat-bubble__shared-post-excerpt">
                    {message.sharedPost.content.slice(0, 80)}{message.sharedPost.content.length > 80 ? '...' : ''}
                  </div>
                )}
                <div className="chat-bubble__shared-post-author">
                  by @{message.sharedPost.authorName || 'User'}
                </div>
              </div>
            </a>
          )}
          {message.content && (
            <div className="chat-bubble__content">
              {message.content.startsWith('↪️ Forwarded:\n') && (
                <div className="chat-bubble__forwarded-header">
                  <IconArrowForwardUp size={14} />
                  <span>Forwarded</span>
                </div>
              )}
              {renderContent(message.content.startsWith('↪️ Forwarded:\n') ? message.content.replace('↪️ Forwarded:\n', '') : message.content)}
            </div>
          )}

          {/* Message Context Menu */}
          {contextMenuVisible && (
            <MessageContextMenu
              position={contextMenuPosition}
              onReply={handleReply}
              onEdit={handleEdit}
              onForward={handleForward}
              onDelete={handleDelete}
              onClose={() => setContextMenuVisible(false)}
              canEdit={isMine}
              canDelete={isMine}
            />
          )}

          {/* Shared Post Context Menu (long-press / right-click) */}
          {sharedPostMenuVisible && (
            <MessageContextMenu
              position={sharedPostMenuPosition}
              onForward={handleSharedPostForward}
              onShare={handleSharedPostShare}
              onClose={() => setSharedPostMenuVisible(false)}
              canEdit={false}
              canDelete={false}
            />
          )}
        </div>

        {/* Meta info OUTSIDE the bubble - Maintains stable height for own messages to prevent layout shift jerks */}
        <div
          className={`chat-message-meta ${isMine ? 'chat-message-meta--mine' : 'chat-message-meta--theirs'}`}
          style={{
            opacity: (showDetails || message.status === 'pending' || message.status === 'failed' || isMine) ? 
                     (isMine && !message.status && !showDetails && tickStatus === 'sent' ? 0.6 : 1) : 0,
            height: (showDetails || message.status === 'pending' || message.status === 'failed' || isMine) ? 'auto' : 0,
            overflow: 'hidden',
            transition: 'opacity 0.2s ease',
            marginTop: (showDetails || message.status === 'pending' || message.status === 'failed' || isMine) ? 4 : 0
          }}
        >
          {showDetails && <span className="chat-message-meta__time">{detailedTime}</span>}

          {isMine && !message.status && (
            <span className={`chat-message-meta__ticks ${tickStatus === 'seen' ? 'chat-message-meta__ticks--seen' :
              tickStatus === 'delivered' ? 'chat-message-meta__ticks--delivered' : ''
              }`}>
              {tickStatus === 'sent' ? '✓' : '✓✓'}
            </span>
          )}
          {message.edited && showDetails && (
            <span className="chat-message-meta__edited">(edited)</span>
          )}
          {message.status === 'pending' && (
            <span className="chat-message-meta__status">Sending...</span>
          )}
          {message.status === 'failed' && (
            <span className="chat-message-meta__failed">
              Failed
              <button
                type="button"
                onClick={(e) => {
                  e.stopPropagation();
                  // FIX M-2: Actually re-send the message instead of dispatching useless event
                  import('../../services/messageQueue').then(({ messageQueue }) => {
                    messageQueue.updateStatus(message.id, 'pending');
                    // Re-trigger send by removing from queue and re-sending via context
                    // The queue subscriber in MessageProvider will pick up the status change
                  });
                }}
                className="chat-message-meta__retry"
              >
                Retry
              </button>
            </span>
          )}
        </div>
      </div>
    </div>
  );
}, arePropsEqual);

// FIX M-11: PropTypes validation
MessageBubble.propTypes = {
  message: PropTypes.shape({
    id: PropTypes.string.isRequired,
    content: PropTypes.string,
    senderId: PropTypes.string,
    createdAt: PropTypes.any,
    status: PropTypes.string,
    deleted: PropTypes.bool,
    edited: PropTypes.bool,
    replyTo: PropTypes.object,
    sharedPost: PropTypes.object,
    imageUrl: PropTypes.string,
    fileUrl: PropTypes.string,
  }).isRequired,
  isGroup: PropTypes.bool,
};

MessageBubble.displayName = 'MessageBubble';

export default MessageBubble;
